import request from "../../utils/request";

/**
 * Every notification type the server seeds, by `NotificationType.Name`.
 *
 * SOURCE OF TRUTH: the server. Ids 1-22, 36-40 and 50-98 come from the
 * `modelBuilder.Entity<NotificationType>().HasData(...)` block in
 * `Mowazaf.Server/Data/BluHRMSContext.cs`; ids 23-35 and 47-49 are inserted
 * directly by migrations (`AddDocumentExpiryNotifications`,
 * `AddLifecycleNotifications`, `AddSubscriptionNotificationFields`,
 * `AddKsaMudadDeadlineNotificationTypes`, `AddCchiEntitiesAndPayslipColumns`).
 * Ids 41-46 are an unused gap. Ids 112-115 are the Engagement Drivers types
 * (CO-147 / CO-153); ids 116-118 are the CO-222 account-inactivity / profile-setup
 * lifecycle types. 99 types as of 2026-08-17.
 *
 * THIS UNION IS THE ONLY PLACE A NEW TYPE HAS TO BE DECLARED CLIENT-SIDE.
 * Adding a name here without also giving it a category and a label in
 * `components/notifications/NotificationPreferenceMatrix.tsx` is a BUILD ERROR
 * (both maps are total over this union). That is deliberate: before this union
 * existed, a seeded type with no matrix entry silently fell into "General" and
 * rendered its raw internal name — which is exactly how type 98
 * (`EncashmentWithdrawn`) and friends rotted unnoticed.
 *
 * The wire DTOs below intentionally keep `notificationTypeName: string`: the
 * server can return a type this bundle has never heard of (it is a deploy
 * behind), and the matrix must degrade gracefully rather than lie to TypeScript.
 */
export type NotificationTypeName =
  // Attendance
  | "ClockInReminder"
  | "ClockOutReminder"
  | "BreakInNotification"
  | "BreakOutNotification"
  | "StillInBreakVerification"
  | "UsersMissingClockInNotification"
  | "EarlyClockOutNotification"
  | "OutOfBoundsClock"
  // Schedule & shifts
  | "ScheduleChanged"
  | "ShiftSwapRequested"
  | "ShiftSwapDecision"
  // Timesheets & overtime
  | "TimeSheetsCreated"
  | "TimesheetSubmitted"
  | "TimesheetDecision"
  | "OvertimeVerification"
  | "OvertimeApprovalRequest"
  | "OvertimeApprovalResponse"
  // Hourly pay (docs/hourly-pay-spec.md §9) — ids 119/120
  | "ShiftPayApprovalDigest"
  | "ShiftPayDecided"
  // Leave & holidays
  | "LeaveRequest"
  | "LeaveRequestResponse"
  | "LeaveRequestUndoResponse"
  | "LeaveApprovalStepPending"
  | "LeaveApprovalStepProgress"
  | "LeaveApprovalEscalated"
  | "LeaveAutoApproved"
  | "LeaveCancellationRequest"
  | "LeaveCancellationDecision"
  | "RecurringLeaveSubmitted"
  | "RecurringLeaveCancelled"
  | "ShortLeaveRequest"
  | "ShortLeaveDecision"
  | "ShortLeaveWithdrawn"
  | "EncashmentRequest"
  | "EncashmentDecision"
  | "EncashmentWithdrawn"
  | "CompOffCreditUpdated"
  | "ReturnToWorkRequired"
  | "ReturnToWorkDocumentsUploaded"
  | "ReturnToWorkCompleted"
  | "CarryForwardExpiring"
  | "HolidayNotification"
  | "RestrictedHolidaySelectionWindow"
  | "LeaveBlackout"
  // Approvals & delegation
  | "AttendanceCorrectionRequestSubmitted"
  | "AttendanceCorrectionRequestDecision"
  | "OutOfBoundsApprovalRequested"
  | "OutOfBoundsApprovalDecision"
  | "DelegationUpdated"
  // Payroll & claims
  | "UserMonthlyPaySlip"
  | "ClaimSubmitted"
  | "ClaimDecision"
  | "ClaimPaid"
  | "GpssaContributionsPending"
  | "KsaMudadDeadlineReminder"
  | "KsaPayrollRunReminder"
  // Assets
  | "AssetRequestSubmitted"
  | "AssetRequestDecision"
  | "AssetRequestFulfilled"
  | "AssetOtpVerification"
  | "AssetReplenishment"
  // Employee lifecycle
  | "OnboardingStarted"
  | "OnboardingStartDateReminder"
  | "OffboardingStarted"
  | "OffboardingLastDayReminder"
  | "InternalMovementInitiated"
  | "InternalMovementPendingManager"
  | "InternalMovementPendingHr"
  | "InternalMovementDecision"
  | "InternalMovementEffectiveDateReminder"
  | "EmployeeMovementSubmitted"
  | "EmployeeMovementDecision"
  | "BusinessRoleAssigned"
  | "BusinessRoleRevoked"
  | "PublicProfileSubmitted"
  | "PublicProfileDecision"
  // Account lifecycle — ids 116/117 (CO222AddInactivityLifecycleSignal) and 118
  // (CO222AddProfileSetupNudgeNotificationType). Seeded by migration since 2026-08-16 but missing
  // here until CO-240, so they fell through to "General" and rendered their raw internal names.
  | "AccountInactivityReminder"
  | "AccountInactivityFinalNotice"
  | "PublicProfileSetupNudge"
  // Documents & compliance
  | "DocumentExpiryReminder"
  | "DocumentExpired"
  | "CchiExpiryReminder"
  | "DependentInsuranceUpdated"
  // Performance & engagement
  | "PerformanceSnapshotSubmitted"
  | "PerformanceSnapshotAcknowledged"
  | "GoalBlockerUpdated"
  | "ReflectionManagerResponse"
  | "MentorAssignmentUpdated"
  | "CheckInUpdated"
  | "CheckInReminder"
  | "CheckInReminderDigest"
  | "RecognitionGapReminder"
  | "Q12Nudge"
  // Projects (Workspaces 128-130, Task Boards 131-136, mentions 137)
  | "WorkspaceStaffingRequired"
  | "WorkspaceMilestoneBilling"
  | "WorkspaceMilestoneOverdue"
  | "TaskAssigned"
  | "TaskSlaBreached"
  | "ChecklistApprovalRequested"
  | "ChecklistItemRejected"
  | "TaskCommentAdded"
  | "TaskDueToday"
  | "TaskMentioned"
  // Tournaments
  | "Tournament"
  // Subscription
  | "SubscriptionTrialEndingSoon"
  | "SubscriptionTrialExpired"
  | "SubscriptionPaidRenewalDue"
  | "SubscriptionPaidOverdue"
  | "SubscriptionUpgradeRequest"
  | "SubscriptionHrUpgradeNudge"
  | "PlatformSubscriptionActivity"
  // Platform, Operator-only (CO-846 mail queue digest, id 127; in-app and push only)
  | "MailQueueAlert"
  // Billing (self-serve billing, ids 99-110; seeded by BillingSeedData). 101/102/109 are E13 must-send
  // (Transactional) and never appear on a preference screen, but are named so an API list still labels.
  | "BillingCheckoutStarted"
  | "BillingPaymentSucceeded"
  | "BillingPaymentFailed"
  | "BillingDunningReminder"
  | "BillingSubscriptionActivated"
  | "BillingSubscriptionRenewed"
  | "BillingSubscriptionCancelled"
  | "BillingPlanChanged"
  | "BillingInvoiceIssued"
  | "BillingSeatCountChanged"
  | "BillingAccessRestricted"
  | "BillingCardExpiringSoon"
  // CO-375 / E13 new ids — give a type id to email senders that already sent (NotificationCatalog 125/126)
  | "PayrollPaid"
  | "UserDeletedNotice"
  // General
  | "OnDemandNotification";

// Types
/**
 * `source` explains a lock (CO-375 / E13): "admin" = company default or master switch off; "module" = the
 * company's notification module for this type is not On; "inApp" = push follows in-app, which is off.
 */
export interface ChannelPreference {
  enabled: boolean;
  source: "default" | "user" | "admin" | "module" | "inApp";
  locked: boolean;
  /** Why it is locked. For "module"/"inApp" locks, enabled/source still carry the STORED choice so a save
   *  echoes it back; render a locked channel as off regardless of enabled. */
  lockedBy?: "admin" | "module" | "inApp" | null;
}

/** E13 notification class. Only "Module" types are switchable; the rest are hidden from every screen. */
export type NotificationClassName = "Module" | "Transactional" | "HumanInitiated" | "Platform";

export interface NotificationPreferenceView {
  notificationTypeId: number;
  notificationTypeName: string;
  email: ChannelPreference;
  push: ChannelPreference;
  inApp: ChannelPreference;
  /** E13 module key (absent from a server older than E13). */
  module?: string | null;
  class?: NotificationClassName;
}

export interface CompanyNotificationSettingView {
  notificationTypeId: number;
  notificationTypeName: string;
  emailsDisabled: boolean;
  mobilePushNotificationsDisabled: boolean;
  inAppDisabled: boolean;
  /** E13 module key; null for must-send / platform / human-initiated types. */
  module?: string | null;
  /** The API returns EVERY type (test muting + sandbox depend on it); screens show only "Module". */
  class?: NotificationClassName;
}

export interface NotificationPreferenceItemInput {
  notificationTypeId: number;
  emailEnabled: boolean | null;
  pushEnabled: boolean | null;
  inAppEnabled: boolean | null;
}

export interface UserNotificationSummaryView {
  userId: string;
  fullName: string;
  email: string | null;
  department: string | null;
  branch: string | null;
  role: string | null;
  customOverrideCount: number;
}

export interface UserNotificationSummaryListView {
  users: UserNotificationSummaryView[];
  totalCount: number;
}

interface Result<T> {
  payload: T;
  status: number;
  errors: Array<{ description: string }>;
}

// User preferences
export async function getUserPreferences() {
  const response = await request.get<Result<NotificationPreferenceView[]>>(
    "NotificationPreference/user"
  );
  return response.data;
}

export async function updateUserPreferences(
  preferences: NotificationPreferenceItemInput[]
) {
  const response = await request.put<Result<boolean>>(
    "NotificationPreference/user",
    { preferences }
  );
  return response.data;
}

// Admin: company settings
export async function getCompanyNotificationSettings() {
  const response = await request.get<Result<CompanyNotificationSettingView[]>>(
    "NotificationPreference/company"
  );
  return response.data;
}

export async function updateCompanyNotificationSettings(
  settings: {
    notificationTypeId: number;
    emailsDisabled: boolean;
    mobilePushNotificationsDisabled: boolean;
    inAppDisabled: boolean;
  }[]
) {
  const response = await request.put<Result<boolean>>(
    "NotificationPreference/company",
    { settings }
  );
  return response.data;
}

// Admin: user management
export async function getUsersWithPreferences(params: {
  departmentId?: number;
  branchId?: number;
  role?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  const response = await request.get<Result<UserNotificationSummaryListView>>(
    "NotificationPreference/users",
    { params }
  );
  return response.data;
}

export async function getUserPreferencesById(userId: string) {
  const response = await request.get<Result<NotificationPreferenceView[]>>(
    `NotificationPreference/users/${userId}`
  );
  return response.data;
}

export async function updateUserPreferencesById(
  userId: string,
  preferences: NotificationPreferenceItemInput[]
) {
  const response = await request.put<Result<boolean>>(
    `NotificationPreference/users/${userId}`,
    { preferences }
  );
  return response.data;
}

export async function bulkUpdatePreferences(input: {
  filter?: {
    departmentId?: number;
    branchId?: number;
    role?: string;
    search?: string;
  };
  preferences: NotificationPreferenceItemInput[];
}) {
  const response = await request.put<Result<boolean>>(
    "NotificationPreference/users/bulk-update",
    input
  );
  return response.data;
}
